import { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '../firebase/config';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { setSentryUser } from '../utils/sentry';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Add a timeout to ensure loading doesn't block forever
    const timeout = setTimeout(() => {
      console.warn('Auth initialization timeout - proceeding without auth state');
      setLoading(false);
    }, 5000); // 5 second timeout

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      clearTimeout(timeout); // Clear timeout once auth state is determined
      
      if (currentUser) {
        try {
          const userRef = doc(db, 'users', currentUser.uid);
          const userDoc = await getDoc(userRef);

          if (!userDoc.exists()) {
            // Only write non-protected fields here. Quota / paid / subscription
            // fields are owned by the Cloud Functions (Stripe webhooks, AI quota).
            await setDoc(userRef, {
              email: currentUser.email || '',
              createdAt: new Date().toISOString()
            });
            setUser({
              ...currentUser,
              paid: false,
              getIdToken: () => currentUser.getIdToken(),
            });
          } else {
            const paid = userDoc.data().paid === true;
            setUser({
              ...currentUser,
              paid,
              getIdToken: () => currentUser.getIdToken(),
            });
          }
        } catch (err) {
          console.error('Error fetching user paid status:', err);
          setUser({
            ...currentUser,
            paid: false,
            getIdToken: () => currentUser.getIdToken(),
          });
        }
      } else {
        setUser(null);
      }
      // Mirror auth state into Sentry for better issue triage (no-op if Sentry off).
      setSentryUser(currentUser ? { uid: currentUser.uid, email: currentUser.email } : null);
      setLoading(false);
    });
    
    return () => {
      clearTimeout(timeout);
      unsubscribe();
    };
  }, []);

  const logout = () => signOut(auth);

  return (
    <AuthContext.Provider value={{ user, loading, logout }}>
      {loading ? (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
            <p className="text-gray-600">Loading...</p>
          </div>
        </div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}